// FE mirror of BE description-deriver (RETINA-7469053).
// Strips markdown / allowed inline HTML (`<u>`) from richDescription and
// trims to 200 chars. Used at save-time to send both `richDescription` and
// the derived short `description` in the same mutation (per PO agreement).
//
// IMPLEMENTATION: we delegate markdown parsing to `markdown-it` configured
// identically to `tiptap-markdown` ({ html: true, linkify: false, breaks:
// false }) - this is the same library tiptap-markdown uses under the hood,
// so the resulting plain text matches exactly what the renderer shows.
// The earlier regex-based stripper produced wrong output for asymmetrical
// markdown (e.g. `**Utek[TEST](url)*Btekst***` left stray `*` characters
// because the regexes assumed perfectly balanced delimiters).

import MarkdownIt from "markdown-it";

export const MAX_RICH_DESCRIPTION_LENGTH = 1000;
export const MAX_SHORT_DESCRIPTION_LENGTH = 200;

const md = new MarkdownIt({
  html: true,
  linkify: false,
  breaks: false,
});

const HTML_TAG_RE = /<[^>]+>/g;
// Minimal HTML entity decoder - covers what markdown-it emits when it
// encounters raw `<` / `>` that don't belong to a recognised tag.
const HTML_ENTITY_MAP = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
};
const HTML_ENTITY_RE = /&(?:amp|lt|gt|quot|#39|apos|nbsp);/g;
const NUMERIC_ENTITY_RE = /&#(\d+);/g;
const HEX_ENTITY_RE = /&#x([0-9a-f]+);/gi;

function decodeEntities(text) {
  return text
    .replace(HTML_ENTITY_RE, (m) => HTML_ENTITY_MAP[m] || m)
    .replace(NUMERIC_ENTITY_RE, (_m, code) => String.fromCodePoint(Number(code)))
    .replace(HEX_ENTITY_RE, (_m, hex) => String.fromCodePoint(parseInt(hex, 16)));
}

/**
 * Strip rich description (markdown + allowed inline HTML) to plain text and
 * trim to maxLength characters. Surrogate-pair safe.
 *
 * @param {string | null | undefined} richDescription
 * @param {number} [maxLength=200]
 * @returns {string}
 */
export function deriveShortDescription(richDescription, maxLength = MAX_SHORT_DESCRIPTION_LENGTH) {
  if (!richDescription) {
    return "";
  }
  const html = md.render(String(richDescription));
  let text = html.replace(HTML_TAG_RE, "");
  text = decodeEntities(text);
  text = text.replace(/\s+/g, " ").trim();

  if (text.length === 0) {
    return "";
  }

  // Surrogate-pair safe trim
  const chars = Array.from(text);
  if (chars.length <= maxLength) {
    return text;
  }
  return chars.slice(0, maxLength).join("");
}

/**
 * Returns true when the value is effectively empty (null, undefined,
 * empty string or whitespace-only markdown).
 *
 * @param {string | null | undefined} richDescription
 * @returns {boolean}
 */
export function isRichDescriptionEmpty(richDescription) {
  if (!richDescription) {
    return true;
  }
  return String(richDescription).trim().length === 0;
}
